// src/components/field/FieldImageryToggle.jsx
//
// The imagery control for /field/route and /field/site. A floating pill over
// the map, sized for a gloved thumb, with a date list when there is more than
// one acquisition.
//
// ── THREE STATES, BECAUSE A BOOLEAN IS WRONG ──────────────────────────────
//
// Earl O&G holds all three across dates of a SINGLE slug:
//
//   available   scope='full'  — the whole mosaic          (2025_12_13)
//   clipped     scope='aoi'   — mosaic cut to a coverage  (2026_03_01)
//                               AOI; tiles outside it are
//                               blank, and blank is
//                               indistinguishable from a
//                               loading failure unless the
//                               control says so
//   unavailable               — no entitlement for this   (2025_09_18)
//                               date, or the mint 403'd
//
// The clipped state is the one that earns its place. A user who toggles
// imagery on and sees photography over the pad and grey 200 m away has no way
// to tell "you did not buy this ground" from "the tiles have not loaded". The
// control says which.
//
// ── UNAVAILABLE IS SHOWN, NOT HIDDEN ──────────────────────────────────────
//
// Same rule the asset list follows for ungranted categories: a control that
// vanishes teaches nothing, and the person concludes the app is broken. A
// disabled control naming the reason is answerable — the rep can tell their
// administrator which date they could not open.

import { Layers, Lock, Scissors, AlertTriangle, Loader2 } from "lucide-react";
import { formatAcquisitionDate } from "../../layers/imagery/formatImageryDate";

function dateLabel(d) {
  try {
    const s = formatAcquisitionDate(d);
    if (s) return s;
  } catch { /* fall through to the raw key */ }
  return d;
}

export default function FieldImageryToggle({ imagery, className = "" }) {
  const { state, on, toggle, acquisitions, active, setActiveKey, error } = imagery;

  const disabled = state === "unavailable" || state === "loading";

  const icon = state === "loading"
    ? <Loader2 size={22} className="animate-spin" />
    : state === "unavailable"
      ? (error ? <AlertTriangle size={22} /> : <Lock size={22} />)
      : state === "clipped"
        ? <Scissors size={22} />
        : <Layers size={22} />;

  const label = state === "loading"
    ? "Checking imagery"
    : state === "unavailable"
      ? "Imagery unavailable"
      : on
        ? (state === "clipped" ? "Imagery on — part only" : "Imagery on")
        : "Imagery";

  return (
    <div className={`pointer-events-auto flex flex-col items-end gap-2 ${className}`}>
      {/* Date list. Only when there is a choice to make — one acquisition needs
          no picker, and a picker with one entry is chrome. */}
      {on && acquisitions.length > 1 && (
        <div className="flex flex-col gap-1 rounded-2xl bg-slate-950/95 border-2 border-slate-700 p-1.5 max-w-[70vw]">
          {acquisitions.map((a) => {
            const sel = a.dataset_key === active?.dataset_key;
            return (
              <button
                key={a.dataset_key}
                type="button"
                onClick={() => setActiveKey(a.dataset_key)}
                aria-pressed={sel}
                className={`min-h-[44px] px-4 rounded-xl text-[15px] font-bold text-left flex items-center gap-2 ${
                  sel ? "bg-cyan-400 text-slate-950" : "bg-slate-900 text-slate-200 active:bg-slate-700"
                }`}
              >
                <span className="truncate">{dateLabel(a.acquisition_date)}</span>
                {a.scope === "aoi" && (
                  <Scissors size={15} className="shrink-0" aria-label="part of this date only" />
                )}
              </button>
            );
          })}
        </div>
      )}

      <button
        type="button"
        onClick={toggle}
        disabled={disabled}
        aria-pressed={on}
        aria-describedby={disabled ? "field-imagery-why" : undefined}
        className={`min-h-[56px] px-5 rounded-2xl border-2 flex items-center gap-3 text-[17px] font-black ${
          disabled
            ? "bg-slate-900 border-slate-800 text-slate-500"
            : on
              ? "bg-cyan-400 border-cyan-200 text-slate-950 active:bg-cyan-300"
              : "bg-slate-950/95 border-slate-700 text-white active:bg-slate-800"
        }`}
      >
        {icon}
        <span>{label}</span>
      </button>

      {/* The clipped explanation is on screen, not in a tooltip — nothing
          hovers on a phone. */}
      {on && state === "clipped" && (
        <p className="max-w-[62vw] text-right text-[14px] font-semibold text-amber-300 leading-snug">
          This date is licensed for part of the map only. Outside that area the
          image is blank — that is the license, not a loading failure.
        </p>
      )}

      {disabled && state === "unavailable" && (
        <p
          id="field-imagery-why"
          className="max-w-[62vw] text-right text-[14px] font-semibold text-amber-300 leading-snug"
        >
          {error
            // The mint refused. Named rather than smoothed over: see the four
            // pre-flight divergences in useFieldImagery.
            ? `Imagery was refused by the server (${error}). Your organization may have imagery switched off, or your account may be resolving to a different organization.`
            : "No imagery date is licensed to this organization here. Contact your administrator."}
        </p>
      )}
    </div>
  );
}
